import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '../../stores/root_store';
import { teamAdminApi, type FHTeamSummary } from '../../api/team_admin_api';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

export const ManagerTeamsPage = observer(() => {
  const navigate = useNavigate();
  const authStore = useAuthStore();

  const [teams, setTeams] = useState<FHTeamSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const currentUserHasManager = authStore.roles.includes('manager');

  const loadTeams = useCallback(async (nextCursor?: string) => {
    const isLoadMore = !!nextCursor;
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await teamAdminApi.list(nextCursor);
      setTeams((prev) => (isLoadMore ? [...prev, ...response.items] : response.items));
      setCursor(response.cursor);
      setHasMore(response.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  // Redirect if not a manager
  if (authStore.initialized && !currentUserHasManager) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/manager')}
        sx={{ mb: 2 }}
      >
        Back to Manager Tools
      </Button>

      <Typography variant="h4" gutterBottom>
        Teams
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Legacy teamwork teams (one billing owner per team).
      </Typography>

      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : teams.length === 0 ? (
            <Alert severity="info">No teams found.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team</TableCell>
                      <TableCell>Billing Owner</TableCell>
                      <TableCell align="right">Members</TableCell>
                      <TableCell>Billing</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow
                        key={team.key}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/manager/teams/${team.key}`)}
                      >
                        <TableCell>{team.team_name || '(untitled)'}</TableCell>
                        <TableCell>
                          {team.master_user_name || team.master_user_email || '—'}
                        </TableCell>
                        <TableCell align="right">{team.member_count}</TableCell>
                        <TableCell>
                          {team.is_billing ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip label="Off" size="small" />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(team.date_created)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => cursor && loadTeams(cursor)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
